import React, { FC, useEffect, useMemo, useState } from 'react'
import Link from '@mui/material/Link'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import { ExportFormat, colors } from '@hazadapt-git/public-core-base'
import { makeStyles } from 'tss-react/mui'
import { primaryIconSize } from '../../lib/styles/universal'
import { useWindowSizeUp } from '../../lib/utils'
import { AnimatedChevron } from '../atoms'

interface InsightQueryViewProps {
    id: string
    name: string
    // email: string
    profilePictureURI?: string
    description: string
    area: string
    dateRange: string
    timestamp: string
    openQueryHref: string
    onDownloadReportPress(id: string, format?: ExportFormat): void
}

export const InsightQueryView: FC<InsightQueryViewProps> = ({
    id,
    name,
    profilePictureURI,
    description,
    area,
    dateRange,
    timestamp,
    openQueryHref,
    onDownloadReportPress,
}: InsightQueryViewProps) => {
    const { classes: localClasses } = useLocalStyles()
    const lgScreens = useWindowSizeUp('lg')

    const [expanded, setExpanded] = useState(false)

    const descriptionText = useMemo(() => {
        return description.length > 0
            ? description
            : 'Overall HazAdapt Activity'
    }, [description])

    useEffect(() => {
        setExpanded(false)
    }, [lgScreens])

    const InsightQueryActions = useMemo(
        () => (
            <div className={localClasses.actionContainer}>
                <Link href={openQueryHref} className={localClasses.link}>
                    Open Query
                </Link>
                <Link
                    component="button"
                    variant="body1"
                    onClick={() => onDownloadReportPress(id)}
                    className={localClasses.link}
                >
                    Download Report
                </Link>
            </div>
        ),
        [localClasses, openQueryHref, onDownloadReportPress, id]
    )

    return (
        <>
            <TableRow
                key={`query-${id}`}
                sx={lgScreens ? null : { '& > *': { borderBottom: 'unset' } }}
            >
                {/* User */}
                <TableCell>
                    <div className={localClasses.rowContainer}>
                        <Avatar
                            sx={{
                                height: '2.5rem',
                                width: '2.5rem',
                                bgcolor: profilePictureURI
                                    ? undefined
                                    : colors.grays.CUMULUS,
                            }}
                            src={profilePictureURI}
                            alt={
                                profilePictureURI
                                    ? `${name}'s profile picture`
                                    : `Default profile picture for ${name}`
                            }
                        />
                        <Typography>{name}</Typography>
                    </div>
                </TableCell>

                {/* Query Description */}
                <TableCell>
                    <Typography fontWeight={500}>{descriptionText}</Typography>
                    <Typography>
                        {area}
                        {dateRange.length > 0 ? ` | ${dateRange}` : ''}
                    </Typography>
                </TableCell>

                {/* Timestamp */}
                {lgScreens && (
                    <TableCell>
                        {timestamp.split('\n').map((chunk, idx) => (
                            <Typography key={`timestamp-chunk-${idx + 1}`}>
                                {chunk}
                            </Typography>
                        ))}
                    </TableCell>
                )}

                {/* Actions */}
                <TableCell>
                    {lgScreens ? (
                        InsightQueryActions
                    ) : (
                        <IconButton onClick={() => setExpanded((ex) => !ex)}>
                            <AnimatedChevron
                                shouldPointUp={expanded}
                                size={primaryIconSize}
                                color={colors.primary.CERULEAN}
                                aria-label={`${
                                    expanded ? 'Hide' : 'Show'
                                } details`}
                            />
                        </IconButton>
                    )}
                </TableCell>
                {/* // TODO: Implement format selection menu */}
            </TableRow>
            {!lgScreens && (
                <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={12}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Stack my="1rem" gap="1rem" alignItems="flex-start">
                                <Typography>
                                    <Typography
                                        fontWeight={500}
                                        component="span"
                                    >
                                        Date Last Used:{' '}
                                    </Typography>
                                    {timestamp.split('\n').join(', ')}
                                </Typography>
                                {InsightQueryActions}
                            </Stack>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}

const useLocalStyles = makeStyles()({
    rowContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    actionContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    cta: {
        whiteSpace: 'nowrap',
    },
    link: {
        fontSize: '1rem',
        lineHeight: '1.25rem',
        width: 'fit-content',
        textAlign: 'left',
    },
})
